import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  addPlant,
  deleteImage,
  updatePlant,
  uploadImage,
  uploadImages,
} from '../../../../api/firebase';
import {
  serviceHealth,
  shrinkFile,
  validateFile,
} from '../../../../utils/fileUtils';
import ModalCarousal from '../../../UI/ModalCarousal';
import PlantForm from '../forms/PlantForm';
import PlantList from './PlantList';

const Plants = (props) => {
  const [editPlant, setEditPlant] = useState(null);
  const [dropDownPlantId, setDropDownPlantId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openCarousal, setOpenCarousal] = useState(false);

  const updatePlantInFb = async (plant) => {
    let imageUrls = [];
    if (
      plant.name !== editPlant.name ||
      plant.description !== editPlant.description ||
      plant.scientificName !== editPlant.scientificName ||
      plant.images.length > 0
    ) {
      props.setLoading(true);
      try {
        if (plant.images.length !== 0) {
          imageUrls = await uploadImages(plant.images);
          editPlant.images.push(...imageUrls);
        }
        await updatePlant(
          {
            name: plant.name,
            description: plant.description,
            scientificName: plant.scientificName,
            images: editPlant.images,
          },
          plant.id
        );
        props.setLoading(false);
        props.fetchPlants(props.area.id);
        setEditPlant(null);
        return;
      } catch (err) {
        toast.error(err.message, {
          position: toast.POSITION.BOTTOM_LEFT,
        });
        props.setLoading(false);
        return;
      }
    }
    setEditPlant(null);
    toast.error('No change in form!', {
      position: toast.POSITION.BOTTOM_LEFT,
    });
  };

  const addPlantInFb = async (plant) => {
    let imageUrls = [];

    if (!plant.name || !plant.description || !plant.scientificName) {
      toast.error('Please fill the fields', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      return;
    }

    props.setLoading(true);
    try {
      if (plant.images.length !== 0) {
        imageUrls = await uploadImages(plant.images);
      }
      await addPlant({
        areaId: props.area.id,
        areaName: props.area.name,
        name: plant.name,
        description: plant.description,
        scientificName: plant.scientificName,
        images: imageUrls,
        isArchived: false,
      });
      props.fetchPlants(props.area.id);
    } catch (err) {
      toast.error(err.message, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    props.setLoading(false);
  };

  const addUpdatePlant = async (plant) => {
    if (plant.id) {
      await updatePlantInFb(plant);
      return;
    }
    await addPlantInFb(plant);
  };

  const resetEdit = () => {
    setEditPlant(null);
  };

  const removeImage = async (img, p) => {
    props.setLoading(true);
    const index = p.images.findIndex((x) => x.name === img.name);
    if (index > -1) {
      p.images.splice(index, 1); // 2nd parameter means remove one item only
    }

    try {
      await deleteImage(img.name);
      await updatePlant({ images: p.images }, p.id);
      props.fetchPlants(props.area.id);
    } catch (err) {
      toast.error(err.message, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    props.setLoading(false);
  };

  const archivePlant = async (plantId) => {
    props.setLoading(true);
    try {
      await updatePlant({ isArchived: true }, plantId);
      setEditPlant(null);
      props.fetchPlants(props.area.id);
    } catch (err) {
      toast.error(err.message, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    props.setLoading(false);
  };

  const handleChangeArea = async (e, plantId) => {
    const areaId = e.target.value;
    if (areaId === props.area.id) {
      return;
    }

    setDropDownPlantId(null);
    const areaName = props.areaList.find((e) => e.id === areaId).name;
    props.setLoading(true);
    try {
      await updatePlant({ areaId: areaId, areaName: areaName }, plantId);
      setEditPlant(null);
      props.fetchPlants(props.area.id);
    } catch (err) {
      toast.error(err.message, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }
    props.setLoading(false);
  };

  const uploadFile = async (e, plant) => {
    e.preventDefault();
    const file = e.target.files[0];
    const name = file.name;

    const error = validateFile(file, name);

    if (error) {
      toast.error('error in file', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      e.target.value = null;
      return;
    }

    props.setLoading(true);
    const serviceStatus = await serviceHealth();

    if (!serviceStatus) {
      toast.error('service is down', {
        position: toast.POSITION.BOTTOM_LEFT,
      });
      e.target.value = null;
      return;
    }

    try {
      const compressedFile = await shrinkFile(file, name);
      if (compressedFile) {
        try {
          const imageUrl = await uploadImage(
            compressedFile.file,
            compressedFile.name
          );
          plant.images.push(imageUrl);
          await updatePlant({ images: plant.images }, plant.id);
        } catch (err) {
          toast.error(err.message, {
            position: toast.POSITION.BOTTOM_LEFT,
          });
        }
      } else {
        toast.error('service depleted', {
          position: toast.POSITION.BOTTOM_LEFT,
        });
      }
    } catch (err) {
      toast.error(err.message, {
        position: toast.POSITION.BOTTOM_LEFT,
      });
    }

    e.target.value = null;
    props.setLoading(false);
    props.fetchPlants(props.area.id);
  };

  const showImages = (images) => {
    setOpenCarousal(true);
    setSelectedImages(images);
  };

  const closeModal = () => {
    setOpenCarousal(false);
    setSelectedImages([]);
  };

  return (
    <>
      <ModalCarousal
        open={openCarousal}
        images={selectedImages}
        closeModal={closeModal}
      />
      {props.isLoggedin && (
        <PlantForm
          isLoading={props.isLoading}
          isLoggedin={props.isLoggedin}
          isArea={false}
          editPlant={editPlant}
          resetEdit={resetEdit}
          handleForm={addUpdatePlant}
        />
      )}
      <PlantList
        isLoading={props.isLoading}
        isLoggedin={props.isLoggedin}
        fetchedPlants={props.fetchedPlants}
        area={props.area}
        areaList={props.areaList}
        removeImage={removeImage}
        handleChangeArea={handleChangeArea}
        dropDownPlantId={dropDownPlantId}
        setDropDownPlantId={setDropDownPlantId}
        setEditPlant={setEditPlant}
        uploadFile={uploadFile}
        archivePlant={archivePlant}
        showImages={showImages}
      />
    </>
  );
};

export default Plants;
